import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../utils/firebase";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";
import AdminSchemaBasedEditor from "./AdminSchemaBasedEditor";
import { effectiveFieldName } from './dataFieldBinding';

// Component to display computed usage from pre-fetched data
const UsageDisplay = ({ fieldId, usageData, usageLoaded }) => {
  const usage = usageData?.[fieldId];
  
  // Still loading
  if (!usageLoaded) {
    return <CircularProgress size={16} />;
  }

  // Loaded but no usage entry means orphaned
  if (!usage) {
    return (
      <Chip label="Orphaned" size="small" color="error" variant="outlined" />
    );
  }

  const { steps, sections } = usage;
  const hasWriter = steps.length > 0;
  const hasReader = sections.length > 0;

  if (!hasWriter && !hasReader) {
    return (
      <Chip label="Orphaned" size="small" color="error" variant="outlined" />
    );
  }

  const stepsTooltip = hasWriter 
    ? `Written by: ${steps.join(', ')}` 
    : 'No steps write to this field';
  const sectionsTooltip = hasReader 
    ? `Displayed in: ${sections.join(', ')}` 
    : 'No sections display this field';

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title={stepsTooltip} arrow>
        <Chip 
          label={`${steps.length} step${steps.length !== 1 ? 's' : ''}`}
          size="small"
          color={hasWriter ? "primary" : "error"}
          variant="outlined"
        />
      </Tooltip>
      <Tooltip title={sectionsTooltip} arrow>
        <Chip 
          label={`${sections.length} section${sections.length !== 1 ? 's' : ''}`}
          size="small"
          color={hasReader ? "secondary" : "warning"}
          variant="outlined"
        />
      </Tooltip>
    </Box>
  );
};

// Help content component for Data Fields
const DataFieldsHelpContent = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Overview</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        Data Fields are the connection points between Steps (which write data) and Sections (which display data).
        Fields are automatically created when you add Steps or Sections.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Category</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        The category determines how the field is grouped in the Prompt Editor's History Fields dropdown.
        New fields default to the "New" category until you assign them to an appropriate group.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Show in History Fields</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        When enabled, this field appears in the Prompt Editor's History Fields selector.
        Disable this to hide internal or temporary fields from the dropdown.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Usage Information</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        The "Written by Steps" and "Displayed in Sections" fields are auto-populated
        and show which Steps and Sections use this data field.
      </p>
    </Box>

    <Box sx={{ 
      backgroundColor: 'var(--color-warning-background)', 
      border: '1px solid var(--color-warning-border)',
      borderRadius: '4px',
      padding: '12px'
    }}>
      <p style={{ margin: 0, color: 'var(--color-warning-text)', fontSize: 'var(--text-sm)' }}>
        <strong>⚠️ Warning:</strong> Do not delete data fields that are in use by Steps or Sections.
        The ID and display name fields are read-only to prevent breaking existing references.
      </p>
    </Box>

    <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', borderTop: '1px solid var(--color-border)', pt: 2 }}>
      Full documentation: <code>docs/admin-document-builder.md</code>
    </Box>
  </Box>
);

const AdminDataFieldsEditor = () => {
  // Pre-fetch all usage data for sorting and filtering
  const [usageData, setUsageData] = useState({});
  const [usageLoaded, setUsageLoaded] = useState(false);
  
  // Filter states
  const [showHistoryOnly, setShowHistoryOnly] = useState(false);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);

  // Fetch all steps and sections to compute usage upfront
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const [stepsSnapshot, sectionsSnapshot, fieldsSnapshot] = await Promise.all([
          getDocs(collection(db, "documentSteps")),
          getDocs(collection(db, "documentSections")),
          getDocs(collection(db, "documentDataFields")),
        ]);

        // Build lookup: fieldName -> { steps: [...], sections: [...] }
        const usage = {};
        const fields = fieldsSnapshot.docs.map(field => ({ ...field.data(), id: field.id }));
        
        stepsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const output = effectiveFieldName(data, fields, true);
          if (output) {
            if (!usage[output]) usage[output] = { steps: [], sections: [] };
            usage[output].steps.push(data.displayName || data.name || doc.id);
          }
        });

        sectionsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const dataField = effectiveFieldName(data, fields, false);
          if (dataField) {
            if (!usage[dataField]) usage[dataField] = { steps: [], sections: [] };
            usage[dataField].sections.push(data.title || doc.id);
          }
        });

        setUsageData(usage);
        setUsageLoaded(true);
      } catch (error) {
        console.error("Error fetching usage data:", error);
        setUsageLoaded(true);
      }
    };

    fetchUsageData();
  }, []);

  // Helper to check if a field has issues (mismatched step/section counts or orphaned)
  const hasIssues = (item) => {
    const fieldId = item.fieldName || item.id;
    const usage = usageData[fieldId];
    if (!usage) return true; // No usage data means orphaned
    const stepCount = usage.steps.length;
    const sectionCount = usage.sections.length;
    // Issues: orphaned (both 0), or mismatched counts
    return (stepCount === 0 && sectionCount === 0) || stepCount !== sectionCount;
  };

  // Custom filter function
  const customFilter = (item) => {
    // Filter by history visibility
    if (showHistoryOnly && item.showInHistoryFields === false) {
      return false;
    }
    // Filter by issues
    if (showIssuesOnly && usageLoaded && !hasIssues(item)) {
      return false;
    }
    return true;
  };

  // Header controls component
  const HeaderControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={showHistoryOnly ? "Show all fields" : "Show history fields only"} arrow>
        <IconButton 
          onClick={() => setShowHistoryOnly(!showHistoryOnly)}
          size="small"
          sx={{ 
            color: showHistoryOnly ? '#1976d2' : 'var(--color-text-secondary)',
            backgroundColor: showHistoryOnly ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: showHistoryOnly ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          {showHistoryOnly ? <Eye size={18} /> : <EyeOff size={18} />}
        </IconButton>
      </Tooltip>
      <Tooltip title={showIssuesOnly ? "Show all fields" : "Show fields with issues only"} arrow>
        <IconButton 
          onClick={() => setShowIssuesOnly(!showIssuesOnly)}
          size="small"
          sx={{ 
            color: showIssuesOnly ? '#ed6c02' : 'var(--color-text-secondary)',
            backgroundColor: showIssuesOnly ? 'rgba(237, 108, 2, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: showIssuesOnly ? 'rgba(237, 108, 2, 0.2)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <AlertTriangle size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Configuration for the Data Fields editor
  const dataFieldsConfig = useMemo(() => ({
    collectionName: "documentDataFields",
    schemaKey: "dataFieldSchema",
    entityName: "Data Field",
    entityNamePlural: "Data Fields",
    searchFields: ['fieldName'],
    idPlaceholder: "chief_complaint",
    disableCreate: true,
    requireDeleteConfirmation: true,
    disableVersioning: true,
    hideDocumentId: true,
    editableIdentityField: true,
    HeaderControls,
    customFilter,
    columns: [
      {
        key: "displayName",
        label: "Display Name",
        render: (item) => (
          <span className="table-primary-text" style={{ color: 'var(--color-primary)' }}>
            {item.displayName || item.fieldName || item.id}
          </span>
        )
      },
      {
        key: "fieldName",
        label: "Field Name",
        render: (item) => (
          <code style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {item.fieldName || item.id}
          </code>
        )
      },
      {
        key: "category",
        label: "Category",
        render: (item) => {
          const category = item.category || "New";
          const isNew = category === "New";
          if (isNew) {
            return <span style={{ color: 'var(--color-text-secondary)' }}>—</span>;
          }
          return (
            <Chip 
              label={category}
              size="small"
              color="default"
              variant="outlined"
            />
          );
        }
      },
      {
        key: "showInHistoryFields",
        label: "History",
        headerTooltip: "Available as a field for History AI processing",
        render: (item) => {
          const show = item.showInHistoryFields !== false;
          return (
            <Tooltip title={show ? "Appears in History Fields dropdown" : "Hidden from History Fields dropdown"} arrow>
              <Chip 
                label={show ? "Yes" : "No"}
                size="small"
                color={show ? "success" : "default"}
                variant="outlined"
              />
            </Tooltip>
          );
        }
      },
      {
        key: "usage",
        label: "Usage",
        sortValue: (item) => {
          // Sort by issue severity: orphaned first, then mismatched, then good
          const fieldId = item.fieldName || item.id;
          const usage = usageData[fieldId];
          if (!usage) return 0; // Orphaned - highest priority
          const stepCount = usage.steps.length;
          const sectionCount = usage.sections.length;
          if (stepCount === 0 && sectionCount === 0) return 0; // Orphaned
          if (stepCount !== sectionCount) return 1; // Mismatched
          return 2; // Good - lowest priority when sorting for issues
        },
        render: (item) => <UsageDisplay fieldId={item.fieldName || item.id} usageData={usageData} usageLoaded={usageLoaded} />
      }
    ],
    HelpContent: DataFieldsHelpContent
  }), [usageData, usageLoaded, showHistoryOnly, showIssuesOnly]);

  return <AdminSchemaBasedEditor config={dataFieldsConfig} />;
};

export default AdminDataFieldsEditor;
